/**
 * Composant d'affichage de la review owner → renter
 * Affiche l'évaluation reçue du propriétaire dans les réservations du locataire
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Rating,
  Paper,
  Stack,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  alpha,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  RateReview as RateReviewIcon,
  Build as EquipmentIcon,
  Schedule as PunctualityIcon,
  Chat as CommunicationIcon,
  Assignment as RulesIcon,
  Inventory as ReturnIcon,
  Star as StarIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Cancel as CancelIcon,
  HourglassEmpty as PendingIcon,
  CheckCircle as ApprovedIcon,
  Block as RejectedIcon,
  Warning as ChangesRequestedIcon,
  PhotoCamera as PhotoCameraIcon,
  ArrowBackIosNew as PrevPhotoIcon,
  ArrowForwardIos as NextPhotoIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import ReportReviewButton from '../ReportReviewButton';
import requestDeduplicator from '../../utils/requestDeduplicator';
import {
  getOwnerReviewFromCache,
  setOwnerReviewCache,
  getShortCacheTtl
} from '../../services/cache/reviewCacheService';

const criteriaConfig = {
  equipment_respect: { icon: EquipmentIcon, color: '#2196f3', key: 'equipment_respect' },
  punctuality: { icon: PunctualityIcon, color: '#4caf50', key: 'punctuality' },
  communication: { icon: CommunicationIcon, color: '#ff9800', key: 'communication' },
  return_state: { icon: ReturnIcon, color: '#9c27b0', key: 'return_state' },
  rules_respect: { icon: RulesIcon, color: '#f44336', key: 'rules_respect' }
};

const moderationStatusConfig = {
  pending: {
    label: 'moderation.pending',
    color: 'warning',
    icon: PendingIcon
  },
  approved: {
    label: 'moderation.approved',
    color: 'success',
    icon: ApprovedIcon
  },
  rejected: {
    label: 'moderation.rejected',
    color: 'error',
    icon: RejectedIcon
  },
  changes_requested: {
    label: 'moderation.changes_requested',
    color: 'info',
    icon: ChangesRequestedIcon
  }
};

const OwnerReviewForRenter = ({ reservation }) => {
  const theme = useTheme();
  const { t } = useTranslation('renterEvaluation');
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [photoError, setPhotoError] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // Résoudre would_recommend depuis criteria_scores OU depuis le champ top-level
  const getWouldRecommend = () => {
    if (!review) return null;
    if (review.criteria_scores?.would_recommend != null) return review.criteria_scores.would_recommend;
    if (review.would_recommend != null) return review.would_recommend;
    return null;
  };

  // Fonction pour obtenir le statut de modération
  const getModerationStatus = () => {
    if (!review) return 'approved';
    return review.moderation_status || 'approved';
  };

  useEffect(() => {
    let isMounted = true;

    const setReviewWithCache = (value, { cache = true, ttl } = {}) => {
      if (!isMounted) {
        return;
      }
      setReview(value);
      if (cache && reservation?.id) {
        setOwnerReviewCache(
          reservation.id,
          value ?? null,
          ttl ?? (value ? undefined : getShortCacheTtl())
        );
      }
    };

    const fetchReview = async () => {
      if (!reservation?.id) {
        setReviewWithCache(null, { cache: false });
        setLoading(false);
        return;
      }

      if (reservation.status !== 'completed') {
        setReviewWithCache(null);
        setLoading(false);
        return;
      }

      if (reservation.ownerReview !== undefined) {
        setReviewWithCache(reservation.ownerReview);
        setLoading(false);
        return;
      }

      const cachedValue = getOwnerReviewFromCache(reservation.id);
      if (cachedValue !== null) {
        setReviewWithCache(cachedValue, { cache: false });
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Utiliser le deduplicator pour éviter les appels concurrents
        const dedupKey = `owner-review-renter:${reservation.id}`;
        const response = await requestDeduplicator.fetch(
          dedupKey,
          () => api.get(`/reviews/renter/${reservation.id}`),
          { debug: process.env.NODE_ENV === 'development' }
        );
        
        const reviewData = response.data.data.data;
        setReviewWithCache(reviewData);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error fetching renter review:', error);
        }
        setReviewWithCache(null, { ttl: getShortCacheTtl() });
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchReview();

    return () => {
      isMounted = false;
    };
  }, [reservation?.id, reservation?.status, reservation?.ownerReview]);

  // Réinitialiser les photos quand la review change
  useEffect(() => {
    setPhotos([]);
    setCurrentPhotoIndex(0);
    setPhotoError(null);
  }, [review?.id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!review) {
    return (
      <Accordion
        disabled
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: alpha(theme.palette.grey[500], 0.05),
          '&:before': { display: 'none' },
          '&.Mui-disabled': { backgroundColor: alpha(theme.palette.grey[500], 0.05) }
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <RateReviewIcon sx={{ color: theme.palette.grey[400] }} />
            <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
              {t('ownerReviewTitle')}
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {t('noReview')}
          </Typography>
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <>
      <Accordion
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: alpha(theme.palette.primary.main, 0.02),
          '&:before': { display: 'none' },
          '&:hover': {
            borderColor: theme.palette.primary.main,
            boxShadow: 1
          }
        }}
      >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%', flexWrap: 'wrap' }}>
          <RateReviewIcon sx={{ color: theme.palette.primary.main }} />
          <Typography variant="subtitle2" fontWeight={600}>
            {t('ownerReviewTitle')}
          </Typography>
          
          {/* Badge de statut de modération */}
          {(() => {
            const status = getModerationStatus();
            const config = moderationStatusConfig[status];
            const StatusIcon = config.icon;
            return (
              <Chip
                size="small"
                icon={<StatusIcon sx={{ fontSize: 16 }} />}
                label={t(config.label)}
                color={config.color}
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
            );
          })()}

          {/* Badge recommandation */}
          {getWouldRecommend() != null && (
            <Chip
              size="small"
              icon={
                getWouldRecommend()
                  ? <CheckIcon sx={{ fontSize: 14 }} />
                  : <CancelIcon sx={{ fontSize: 14 }} />
              }
              label={getWouldRecommend() ? t('recommended') : t('notRecommended')}
              color={getWouldRecommend() ? 'success' : 'error'}
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          )}
          
          <Chip
            icon={<StarIcon sx={{ fontSize: 14 }} />}
            label={`${review.rating}/5`}
            size="small"
            color="warning"
            sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600, ml: 'auto' }}
          />
          
          {/* Bouton de signalement */}
          <ReportReviewButton
            reviewType="owner_review"
            reviewId={review.id}
            variant="icon"
            size="small"
          />
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 2 }}>
        {/* Résumé compact */}
        <Box sx={{ mb: 2 }}>
          {/* Note globale */}
          <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
            <Rating value={review.rating} readOnly size="medium" precision={0.5} />
            <Typography variant="body1" fontWeight={600} color="warning.dark">
              ({review.rating}/5)
            </Typography>
          </Stack>

          {/* Badge recommandation */}
          {getWouldRecommend() != null && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              {getWouldRecommend() ? (
                <CheckIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />
              ) : (
                <CancelIcon sx={{ color: theme.palette.error.main, fontSize: 20 }} />
              )}
              <Typography variant="body2" fontWeight={500}>
                {getWouldRecommend() ? t('recommended') : t('notRecommended')}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Bouton pour ouvrir le modal */}
        <Button
          variant="outlined"
          color="primary"
          onClick={async () => {
            setOpenModal(true);
            if (review?.id && photos.length === 0 && !loadingPhotos) {
              setLoadingPhotos(true);
              setPhotoError(null);
              try {
                const response = await api.get(`/reviews/owner-renter/${review.id}/photos`);
                const photoData = response?.data?.data?.data?.photos || [];
                setPhotos(photoData);
              } catch (err) {
                setPhotoError(t('photos.loadError'));
              } finally {
                setLoadingPhotos(false);
              }
            }
          }}
          fullWidth
          sx={{ mb: 1 }}
        >
          {t('viewDetails')}
        </Button>

        {/* Message informatif */}
        <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
          {t('clickToViewDetails')}
        </Typography>
      </AccordionDetails>
    </Accordion>

    {/* Modal avec tous les détails */}
    <Dialog
      open={openModal}
      onClose={() => setOpenModal(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: `1px solid ${theme.palette.divider}`,
        pb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RateReviewIcon sx={{ color: theme.palette.primary.main }} />
          <Typography variant="h6" fontWeight={600}>
            {t('ownerReviewTitle')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <ReportReviewButton
            reviewType="owner_review"
            reviewId={review.id}
            variant="icon"
            size="small"
          />
          <IconButton
            onClick={() => setOpenModal(false)}
            size="small"
            sx={{ 
              color: theme.palette.grey[500],
              '&:hover': { backgroundColor: alpha(theme.palette.grey[500], 0.1) }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Recommandation complète */}
        {getWouldRecommend() != null && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: getWouldRecommend() 
                ? alpha(theme.palette.success.main, 0.1)
                : alpha(theme.palette.error.main, 0.1),
              border: `1px solid ${
                getWouldRecommend() 
                  ? theme.palette.success.main
                  : theme.palette.error.main
              }`,
              borderRadius: 2
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              {getWouldRecommend() ? (
                <CheckIcon sx={{ color: theme.palette.success.main, fontSize: 28 }} />
              ) : (
                <CancelIcon sx={{ color: theme.palette.error.main, fontSize: 28 }} />
              )}
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  {getWouldRecommend() ? t('renterRecommended') : t('renterNotRecommended')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {getWouldRecommend() 
                    ? t('ownerRecommends')
                    : t('ownerNotRecommends')}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}

        {/* Note globale */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary" mb={1}>
            {t('globalRating')}
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Rating value={review.rating} readOnly size="large" precision={0.5} />
            <Typography variant="h5" fontWeight={600} color="warning.dark">
              {review.rating}/5
            </Typography>
          </Stack>
        </Box>

        {/* Commentaire */}
        {review.comment && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} color="text.secondary" mb={1}>
              {t('comment')}
            </Typography>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontStyle: 'italic',
                  color: theme.palette.text.primary,
                  lineHeight: 1.6
                }}
              >
                "{review.comment}"
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Critères détaillés */}
        {review.criteria_scores && Object.keys(review.criteria_scores).some(key => 
          criteriaConfig[key] && review.criteria_scores[key]?.rating
        ) && (
          <Box>
            <Typography variant="subtitle2" fontWeight={600} color="text.secondary" mb={2}>
              {t('detailedCriteria')}
            </Typography>
            <Stack spacing={2}>
              {Object.keys(criteriaConfig).map((key) => {
                const criteria = review.criteria_scores[key];
                if (!criteria || !criteria.rating) return null;

                const IconComponent = criteriaConfig[key].icon;
                return (
                  <Paper
                    key={key}
                    elevation={0}
                    sx={{
                      p: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      backgroundColor: alpha(criteriaConfig[key].color, 0.03),
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: 1,
                        borderColor: criteriaConfig[key].color
                      }
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: 1.5,
                          backgroundColor: alpha(criteriaConfig[key].color, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <IconComponent sx={{ fontSize: 24, color: criteriaConfig[key].color }} />
                      </Box>

                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
                          {t(`criteria.${key}.title`)}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                          <Rating value={criteria.rating} readOnly size="small" />
                          <Typography variant="body2" fontWeight={500} color={criteriaConfig[key].color}>
                            {criteria.rating}/5
                          </Typography>
                        </Stack>
                        {criteria.comment && (
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                            {criteria.comment}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Box>
        )}

        {/* Date d'évaluation */}
        {/* Photos jointes */}
        {loadingPhotos ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              {t('photos.loading')}
            </Typography>
          </Box>
        ) : photoError ? (
          <Paper elevation={0} sx={{ p: 2, backgroundColor: alpha(theme.palette.warning.main, 0.08), borderRadius: 2 }}>
            <Typography variant="body2" color="warning.dark">{photoError}</Typography>
          </Paper>
        ) : photos.length > 0 ? (
          <Box sx={{ mt: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
              <PhotoCameraIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                {t('photos.count', { count: photos.length })}
              </Typography>
            </Stack>
            <Box sx={{ position: 'relative', width: '100%', height: 280, backgroundColor: alpha(theme.palette.grey[500], 0.1), borderRadius: 2, overflow: 'hidden' }}>
              <Box
                component="img"
                src={photos[currentPhotoIndex]}
                alt={`Photo ${currentPhotoIndex + 1}`}
                sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
              {photos.length > 1 && (
                <>
                  <IconButton
                    onClick={() => setCurrentPhotoIndex(prev => (prev - 1 + photos.length) % photos.length)}
                    sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(0,0,0,0.55)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}
                  >
                    <PrevPhotoIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={() => setCurrentPhotoIndex(prev => (prev + 1) % photos.length)}
                    sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(0,0,0,0.55)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}
                  >
                    <NextPhotoIcon fontSize="small" />
                  </IconButton>
                  <Box sx={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', bgcolor: 'rgba(0,0,0,0.55)', color: 'white', px: 1.5, py: 0.5, borderRadius: 2, fontSize: '0.75rem' }}>
                    {currentPhotoIndex + 1} / {photos.length}
                  </Box>
                </>
              )}
            </Box>
            {photos.length > 1 && (
              <Stack direction="row" spacing={1} sx={{ mt: 1, overflowX: 'auto', pb: 1 }}>
                {photos.map((photo, index) => (
                  <Box
                    key={index}
                    component="img"
                    src={photo}
                    alt={`Miniature ${index + 1}`}
                    onClick={() => setCurrentPhotoIndex(index)}
                    sx={{
                      width: 56, height: 56, objectFit: 'cover', borderRadius: 1, cursor: 'pointer', flexShrink: 0,
                      border: currentPhotoIndex === index ? '2px solid' : '1px solid',
                      borderColor: currentPhotoIndex === index ? 'primary.main' : 'divider',
                      opacity: currentPhotoIndex === index ? 1 : 0.6,
                      transition: 'all 0.2s', '&:hover': { opacity: 1 }
                    }}
                  />
                ))}
              </Stack>
            )}
          </Box>
        ) : null}

        {review.created_at && (
          <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="caption" color="text.secondary" display="block" textAlign="right">
              {t('reviewedOn', {
                date: new Date(review.created_at).toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })
              })}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button
          onClick={() => setOpenModal(false)}
          variant="contained"
          color="primary"
        >
          {t('close')}
        </Button>
      </DialogActions>
    </Dialog>
  </>
  );
};

export default OwnerReviewForRenter;
